import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { MessageContainer } from './common'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import { ModalElementContext } from 'contexts/ModalProvider'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { take } from 'lib/array/arrayUtils'
import CarouselCTACard from 'tripPlanner/components/Bookmark/Carousels/CarouselCTACard'
import ExperienceItemCarousel from 'tripPlanner/components/Bookmark/Carousels/ExperienceItemCarousel'
import ExperienceSearchModalCTA from 'tripPlanner/components/TripSummary/CallToActionButtons/Experience/ExperienceSearchModalCTA'
import { SectionIdContext } from 'tripPlanner/contexts/SectionIdProvider'
import { useLEPlaceByCoordinates } from 'tripPlanner/hooks/api/place'

interface Props {
  lat: number
  lng: number
  tripId: string
  showSearch: boolean
}

function NearbyExperiencesModal({ lat, lng, tripId, showSearch }: Props) {
  const modalElement = useContext<any>(ModalElementContext)
  const intl = useIntl()

  const recommendedOffersTitle = intl.formatMessage({
    id: 'F4-4LIhtFINw3pnrilfJ',
    defaultMessage: 'Recommended Offers',
    description: 'Title for the recommended experiences modal in trip planner',
  })

  const findingOffersMessage = intl.formatMessage({
    id: 'V0YP2VtSS4Zy1qrClQIv',
    defaultMessage: 'Finding the best offers for you',
    description: 'Loading message while fetching recommended experiences',
  })

  const noExperiencesMessage = intl.formatMessage({
    id: 'EETe9oKUjLyd7Z5cen3L',
    defaultMessage: 'Sorry, we can’t find any experiences nearby',
    description: 'Empty state message when no nearby experiences are available',
  })

  const { data: place } = useLEPlaceByCoordinates(lat, lng)
  const sectionId = useContext(SectionIdContext)

  const [list] = useExperienceSearch({
    placeId: place?.id,
    limit: 0,
  })

  const isLoading = (!list || list.fetching) && !list?.error

  const experienceIds = useMemo(() => (list ? take(list.ids, 3) : []), [list])

  const carouselCtaCard = showSearch ? (
    <CarouselCTACard key="ctaCard">
      <ExperienceSearchModalCTA
        tripId={tripId}
        sectionId={sectionId}
        onClick={modalElement.resolve}
      />
    </CarouselCTACard>
  ) : undefined

  return (
    <Modal title={recommendedOffersTitle} onClose={modalElement.resolve} size="L">
      <VerticalSpacer gap={16}>
        {isLoading && (
          <MessageContainer>
            <BodyText
              align="center"
              colour="normal-contrast"
              variant="large"
              weight="bold"
            >
              {findingOffersMessage}
            </BodyText>
            <LoadingIndicator inline />
          </MessageContainer>
        )}
        {!isLoading && experienceIds.length === 0 && (
          <MessageContainer>
            <BodyText
              align="center"
              colour="normal-contrast"
              variant="large"
              weight="bold"
            >
              {noExperiencesMessage}
            </BodyText>
            {showSearch && (
              <ExperienceSearchModalCTA
                tripId={tripId}
                sectionId={sectionId}
                onClick={modalElement.resolve}
              />
            )}
          </MessageContainer>
        )}
        {!isLoading && experienceIds.length > 0 && (
          <ExperienceItemCarousel
            offerIds={experienceIds}
            sectionId={sectionId}
            ctaCard={carouselCtaCard}
          />
        )}
      </VerticalSpacer>
    </Modal>
  )
}

export default NearbyExperiencesModal
